import { useEffect, useMemo, useState } from 'react';
import { Api } from '../../services/api';
import normalizeProducts from '../../services/productNormalizer';
import { resolvePriceInfo, variantBasePrice, formatAmount } from '../../services/pricing';
import { saveSelectedProduct } from '../../services/productSelection';
import heartIcon from '../../assets/heart.png';
import heartFillIcon from '../../assets/heart-fill.png';
import { useLanguage } from '../../context/LanguageContext';
import { getStoredCustomerId } from '../../services/customer';
import { toggleProductFavorite } from '../../services/favorites';
import { redirectToLogin } from '../../services/auth';

const PRICE_RANGE_OPTIONS = [
  { labelKey: 'products_page.price_range_all', value: 'all' },
  { labelKey: 'products_page.price_range_under_200', value: '0-200' },
  { labelKey: 'products_page.price_range_200_500', value: '200-500' },
  { labelKey: 'products_page.price_range_500_1000', value: '500-1000' },
  { labelKey: 'products_page.price_range_1000_plus', value: '1000+' },
];

const SORT_OPTIONS = [
  { labelKey: 'products_page.sort.placeholder', value: 'sort-by', disabled: true },
  { labelKey: 'products_page.sort.all', value: 'all' },
  { labelKey: 'products_page.sort.featured', value: 'featured' },
  { labelKey: 'products_page.sort.new_arrivals', value: 'new-arrivals' },
  { labelKey: 'products_page.sort.price_asc', value: 'price-asc' },
  { labelKey: 'products_page.sort.price_desc', value: 'price-desc' },
  { labelKey: 'products_page.sort.name_asc', value: 'name-asc' },
];

const getInitialCategory = () => {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search);
  return params.get('category') || '';
};

const getInitialBrand = () => {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search);
  return params.get('brand') || '';
};

const getInitialBrandLabel = () => {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search);
  return params.get('brandName') || '';
};

const getInitialCollection = () => {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search);
  const collection = params.get('collection') || '';
  return collection === 'new-arrivals' ? 'new-arrivals' : '';
};

const datasetValues = new Set(['all', 'featured', 'new-arrivals']);

const getLowestPriceVariant = (product) => {
  const variants = product?.variants;
  if (!Array.isArray(variants) || !variants.length) return null;

  return variants.reduce((cheapest, variant) => {
    if (!variant) return cheapest;
    if (!cheapest) return variant;

    const cheapestPrice = variantBasePrice(product, cheapest);
    const candidatePrice = variantBasePrice(product, variant);
    return candidatePrice < cheapestPrice ? variant : cheapest;
  }, null);
};

const ProductsPage = () => {
  const { t } = useLanguage();
  const customerId = useMemo(getStoredCustomerId, []);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(getInitialCategory);
  const [selectedBrand, setSelectedBrand] = useState(getInitialBrand);
  const [brandLabel, setBrandLabel] = useState(getInitialBrandLabel);
  const [selectedCollection, setSelectedCollection] = useState(getInitialCollection);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceFilter, setPriceFilter] = useState('all');
  const [sort, setSort] = useState('all');
  const [datasetMode, setDatasetMode] = useState('all');
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [variantPreview, setVariantPreview] = useState({
    productId: null,
    variant: null,
  });
  const [page, setPage] = useState(1);
  const [favoriteBusyIds, setFavoriteBusyIds] = useState(() => new Set());
  // 12 = full rows with 4, 3 or 2 columns (desktop, tablet, phone)
  const pageSize = 12;

  useEffect(() => {

    let mounted = true;
    Api.fetchCategories()
      .then((data) => {
        if (!mounted) return;
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        const mapped = list.map((item, index) => ({
          id: item.categoryId || item.id || `category-${index}`,
          name: item.name || item.title || '',
        }));
        setCategories(mapped);
      })
      .catch(() => {
        if (mounted) setCategories([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const fetcher = selectedBrand
      ? Api.fetchProductsByBrand(selectedBrand, customerId)
      : selectedCategory
      ? Api.fetchProductsByCategory(selectedCategory, customerId)
      : selectedCollection === 'new-arrivals'
      ? Api.fetchNewArrivals(customerId)
      : datasetMode === 'featured'
      ? Api.fetchFeaturedProducts(customerId)
      : datasetMode === 'new-arrivals'
      ? Api.fetchNewArrivals(customerId)
      : Api.fetchProducts(customerId);

    fetcher
      .then((data) => {
        if (cancelled) return;
        const list = normalizeProducts(data?.data?.data || data?.data || data);
        setProducts(list);
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, selectedCategory, selectedBrand, selectedCollection, datasetMode]);

  const filteredProducts = useMemo(() => {
    let list = [...products];
    if (priceFilter !== 'all') {
      if (priceFilter.endsWith('+')) {
        const min = Number(priceFilter.replace('+', '')) || 0;
        list = list.filter((item) => Number(item.price) >= min);
      } else {
        const [min, max] = priceFilter.split('-').map(Number);
        list = list.filter(
          (item) =>
            Number(item.price) >= min && Number(item.price) <= (max || Infinity)
        );
      }
    }
    switch (sort) {
      case 'price-asc':
        list.sort((a, b) => Number(a.price) - Number(b.price));
        break;
      case 'price-desc':
        list.sort((a, b) => Number(b.price) - Number(a.price));
        break;
      case 'name-asc':
        list.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'all':
      case 'featured':
      default:
        break;
    }
    return list;
  }, [products, priceFilter, sort]);

  const paginatedProducts = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredProducts.slice(start, start + pageSize);
  }, [filteredProducts, page]);

  const totalPages = Math.ceil(filteredProducts.length / pageSize) || 1;

  const selectedCategoryName =
    categories.find((entry) => String(entry.id) === String(selectedCategory))
      ?.name || t('products_page.default_heading');
  const isNewArrivalsView =
    selectedCollection === 'new-arrivals' || datasetMode === 'new-arrivals';
  const isFeaturedView = datasetMode === 'featured';
  const selectedBrandName = selectedBrand ? brandLabel || selectedBrand : '';
  const activeHeaderLabel =
    selectedBrandName ||
    (isNewArrivalsView
      ? t('home.new_arrivals')
      : isFeaturedView
      ? t('products_page.sort.featured')
      : selectedCategoryName);

  useEffect(() => {
    if (selectedCollection === 'new-arrivals' && datasetMode !== 'new-arrivals') {
      setDatasetMode('new-arrivals');
      setSort('new-arrivals');
    } else if (!selectedCollection && datasetMode === 'new-arrivals' && sort === 'new-arrivals') {
      setDatasetMode('all');
      setSort('all');
    }
  }, [datasetMode, selectedCollection, sort]);

  const resetUrlFilters = (nextCollection = '') => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    params.delete('category');
    params.delete('brand');
    params.delete('brandName');
    if (nextCollection) {
      params.set('collection', nextCollection);
    } else {
      params.delete('collection');
    }
    const search = params.toString();
    const nextUrl = search ? `/products?${search}` : '/products';
    window.history.replaceState({}, '', nextUrl);
  };

  const handleChangeCategory = (event) => {
    const value = event.target.value;
    setSelectedCategory(value);
    setSelectedCollection('');
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (value) {
        params.set('category', value);
      } else {
        params.delete('category');
      }
      params.delete('brand');
      params.delete('brandName');
      params.delete('collection');
      const search = params.toString();
      const nextUrl = search ? `/products?${search}` : '/products';
      window.history.replaceState({}, '', nextUrl);
    }
    setPage(1);
    setSelectedBrand('');
    setBrandLabel('');
  };

  const markFavoriteBusy = (productId) => {
    setFavoriteBusyIds((prev) => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  const clearFavoriteBusy = (productId) => {
    setFavoriteBusyIds((prev) => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
  };

  const handleToggleFavorite = async (event, product) => {
    event.stopPropagation();
    if (!customerId) {
      redirectToLogin();
      return;
    }
    if (favoriteBusyIds.has(product.id)) return;
    markFavoriteBusy(product.id);
    try {
      const result = await toggleProductFavorite({ product, customerId });
      setProducts((prev) =>
        prev.map((item) =>
          item.id === product.id
            ? { ...item, favorite: result.favorite, favoriteId: result.favoriteId }
            : item
        )
      );
    } catch {
      // ignore toggle errors
    } finally {
      clearFavoriteBusy(product.id);
    }
  };

  return (
    <div className="products-page">
     
      <h1 className="products-title">{activeHeaderLabel}</h1>
      <header className="products-header">
        <div className="products-header__left">
          <label>
            {t('products_page.category_label')}
            <select value={selectedCategory} onChange={handleChangeCategory}>
              <option value="">{t('products_page.category_all')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name || t('collections.fallback_category')}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('products_page.price_label')}
            <select
              value={priceFilter}
              onChange={(event) => setPriceFilter(event.target.value)}
            >
              {PRICE_RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="products-header__right">
          <p>
            {filteredProducts.length} {t('products_page.products_label')}
          </p>
          <label>
    
            <select
              value={sort}
              onChange={(event) => {
                const value = event.target.value;
                setSort(value);
                if (datasetValues.has(value)) {
                  setDatasetMode(value);
                  setPage(1);
                  setSelectedCategory('');
                  setSelectedBrand('');
                  setBrandLabel('');
                  const nextCollection = value === 'new-arrivals' ? 'new-arrivals' : '';
                  setSelectedCollection(nextCollection);
                  resetUrlFilters(nextCollection);
                }
              }}
            >
              {SORT_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {t(option.labelKey)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {loading ? (
        <p className="products-state">{t('products_page.loading')}</p>
      ) : !filteredProducts.length ? (
        <p className="products-state">{t('products_page.empty')}</p>
      ) : (
        <div className="products-grid">
          {paginatedProducts.map((product) => {
            const isVariantViewActive =
              hoveredProductId === product.id && product.variants?.length;
            const isPreviewingVariant = variantPreview.productId === product.id;
            const previewVariant =
              isPreviewingVariant && variantPreview.variant
                ? variantPreview.variant
                : null;
            const lowestVariant =
              !previewVariant ? getLowestPriceVariant(product) : null;
            const priceInfo = resolvePriceInfo(
              product,
              previewVariant || lowestVariant || null
            );

            const renderPriceRow = () => (
              <div className="product-card__price">
                {priceInfo.showOriginal && (
                  <span className="product-card__old-price">
                    {product.currency}
                    {formatAmount(priceInfo.original)}
                  </span>
                )}
                <span
                  className={`product-card__current-price ${
                    priceInfo.showOriginal
                      ? 'product-card__current-price--discount'
                      : ''
                  }`}
                >
                  {product.currency}
                  {formatAmount(priceInfo.current)}
                </span>
              </div>
            );

            const handleNavigate = () => {
              saveSelectedProduct(product);
              window.location.assign(
                `/product/${encodeURIComponent(product.id || product.productId || '')}`
              );
            };

            const baseImage = product.images?.[0] || product.image;
            return (
              <article
                key={product.id}
                className={`product-card product-card--listing ${
                  isVariantViewActive ? 'product-card--show-variants' : ''
                }`}
                onMouseEnter={() => setHoveredProductId(product.id)}
                onMouseLeave={() => {
                  setHoveredProductId(null);
                  setVariantPreview({
                    productId: null,
                    variant: null,
                  });
                }}
                onClick={handleNavigate}
              >
                <div className="product-card__media">
                  <img
                    src={previewVariant?.image ? previewVariant.image : baseImage}
                    alt={product.name}
                    loading="lazy"
                  />
                  <div className="product-card__actions product-card__actions--overlay">
                    <button
                      type="button"
                      aria-label="Add to wishlist"
                      onClick={(event) => handleToggleFavorite(event, product)}
                      aria-pressed={product.favorite}
                      disabled={favoriteBusyIds.has(product.id)}
                    >
                      <img
                        src={product.favorite ? heartFillIcon : heartIcon}
                        alt=""
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
                <div className="product-card__body">
                  {isVariantViewActive ? (
                    <>
                      <div
                        className="product-card__variants"
                        onMouseLeave={() =>
                          setVariantPreview({
                            productId: null,
                            variant: null,
                          })
                        }
                      >
                        {product.variants
                          .filter((variant, idx, arr) => {
                            return (
                              arr.findIndex(
                                (item) =>
                                  item.colorCode === variant.colorCode &&
                                  item.colorName === variant.colorName
                              ) === idx
                            );
                          })
                          .map((variant) => (
                            <button
                              key={variant.id}
                              type="button"
                              className="variant-thumb"
                              style={
                                variant.colorCode
                                  ? { backgroundColor: variant.colorCode }
                                  : undefined
                              }
                              onMouseEnter={() =>
                                setVariantPreview({
                                  productId: product.id,
                                  variant,
                                })
                              }
                              onClick={(event) => event.stopPropagation()}
                            >
                              {!variant.colorCode && (
                                <img
                                  src={variant.image}
                                  alt="Variant option"
                                />
                              )}
                            </button>
                          ))}
                      </div>
                      {renderPriceRow()}
                    </>
                  ) : (
                    <>
                      <p className="product-card__brand">{product.brand}</p>
                      <h3>{product.name}</h3>
                      {renderPriceRow()}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
      {!loading && filteredProducts.length > 0 && (
        <div className="products-pagination">
          <button type="button" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            &lt;
          </button>
          {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((num) => (
            <button
              key={num}
              type="button"
              className={num === page ? 'is-active' : ''}
              onClick={() => setPage(num)}
            >
              {num}
            </button>
          ))}
          <button
            type="button"
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            &gt;
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
